<template>
    <b-modal
        ref="modal"
        title="Trendyol Ürün Yönetimi"
        size="lg"
        scrollable
        hide-footer
        @hide="onHide"
        @hidden="reset"
    >
        <p
            v-if="loading"
            role="status"
        >
            Ürün yükleniyor…
        </p>
        <b-alert
            :show="!!error"
            variant="danger"
        >
            {{ error }} <b-button
                v-if="!form"
                size="sm"
                @click="open(productId)"
            >
                Tekrar dene
            </b-button>
        </b-alert>
        <b-form
            v-if="form"
            @submit.prevent="save"
        >
            <fieldset :disabled="saving">
                <h5>{{ form.product_name }}</h5>
                <p>Barkod: {{ form.merchant_sku }}</p>
                <b-alert
                    :show="!!success"
                    variant="success"
                >
                    {{ success }}
                </b-alert>
                <p>{{ statusLabel }} · Kaydedilmiş eksik zorunlu özellik sayısı: {{ form.missing_required_attributes_count }}</p>
                <b-form-group label="Kategori">
                    <p>{{ category ? category.name : 'Kategori yok' }}</p>
                    <b-button
                        size="sm"
                        variant="outline-primary"
                        @click="picker = !picker"
                    >
                        Kategori seç / değiştir
                    </b-button>
                    <b-button
                        v-if="category"
                        size="sm"
                        variant="link"
                        @click="chooseCategory(null)"
                    >
                        Kategoriyi kaldır
                    </b-button>
                    <div
                        v-for="message in categoryErrors"
                        :key="message"
                        class="text-danger"
                        role="alert"
                    >
                        {{ message }}
                    </div>
                </b-form-group>
                <b-alert
                    show
                    variant="warning"
                >
                    Kategori değişince mevcut özellik seçimleri silinecek. Değişiklikler Kaydet ile uygulanır.
                </b-alert>
                <category-picker
                    v-if="picker"
                    @select="chooseCategory"
                />
                <p
                    v-if="attributesLoading"
                    role="status"
                >
                    Özellikler yükleniyor…
                </p>
                <b-alert
                    :show="!!attributesError"
                    variant="danger"
                >
                    {{ attributesError }} <b-button
                        size="sm"
                        @click="loadAttributes"
                    >
                        Tekrar dene
                    </b-button>
                </b-alert>
                <b-alert
                    v-if="category && !attributesLoading && !attributesError && !attributes.length"
                    show
                    variant="info"
                >
                    Yerel katalogda bu kategori için özellik tanımı yok. Zorunlu özelliklerin tamamlanması doğrulanamıyor.
                </b-alert>
                <p v-if="!category">
                    Özellik seçmek için bir son kategori seçin.
                </p>
                <attribute-field
                    v-for="attribute in attributes"
                    :key="`${formVersion}-${attribute.attribute_id}`"
                    :attribute="attribute"
                    :category-id="Number(category.ty_id)"
                    :disabled="saving"
                    :errors="attributeErrors[attribute.attribute_id] || []"
                    @change="setSelection(attribute.attribute_id, $event)"
                />
                <p v-if="category && attributes.length">
                    Formda seçilmemiş zorunlu özellik: {{ missingCount }}. Eksik seçimler kaydetmeyi engellemez.
                </p>
            </fieldset>
            <div class="d-flex justify-content-end mt-2">
                <b-button
                    variant="outline-secondary"
                    :disabled="saving"
                    class="mr-1"
                    @click="$refs.modal.hide()"
                >
                    Kapat
                </b-button>
                <b-button
                    variant="primary"
                    type="submit"
                    :disabled="saving || attributesLoading || !!attributesError"
                >
                    {{ saving ? 'Kaydediliyor…' : 'Kaydet' }}
                </b-button>
            </div>
        </b-form>
    </b-modal>
</template>
<script>
    import {
        BModal, BAlert, BButton, BForm, BFormGroup,
    } from 'bootstrap-vue'
    import { classificationLabels, errorMessage } from '@/store/v2/trendyol/management'
    import CategoryPicker from './CategoryPicker.vue'
    import AttributeField from './AttributeField.vue'

    export default {
        components: {
            BModal, BAlert, BButton, BForm, BFormGroup, CategoryPicker, AttributeField,
        },
        data: () => ({
            productId: null,
            form: null,
            category: null,
            attributes: [],
            picker: false,
            loading: false,
            saving: false,
            attributesLoading: false,
            error: '',
            attributesError: '',
            success: '',
            categoryErrors: [],
            attributeErrors: {},
            baseline: '',
            version: 0,
            formVersion: 0,
        }),
        computed: {
            statusLabel() { return classificationLabels[this.form.classification_status] || this.form.classification_status },
            payload() {
                return {
                    category_id: this.category ? Number(this.category.ty_id) : null,
                    attributes: this.category ? this.attributes.reduce((rows, attribute) => {
                        const selection = attribute.selection || {}
                        if (selection.value_id != null) rows.push({ attribute_id: attribute.attribute_id, value_id: selection.value_id })
                        else if (selection.custom_value && selection.custom_value.trim()) rows.push({ attribute_id: attribute.attribute_id, custom_value: selection.custom_value.trim() })
                        return rows
                    }, []) : [],
                }
            },
            dirty() { return !!this.form && JSON.stringify(this.payload) !== this.baseline },
            missingCount() {
                return this.attributes.filter(attribute => attribute.required && !this.payload.attributes.some(row => row.attribute_id === attribute.attribute_id)).length
            },
        },
        mounted() { window.addEventListener('beforeunload', this.beforeUnload) },
        beforeDestroy() { window.removeEventListener('beforeunload', this.beforeUnload); this.version += 1 },
        methods: {
            async open(id) {
                this.version += 1
                const { version } = this
                this.productId = id
                this.form = null
                this.loading = true
                this.error = ''
                this.$refs.modal.show()
                try {
                    const product = await this.$store.dispatch('tyManagement/loadProduct', id)
                    if (version !== this.version) return
                    this.$store.commit('tyManagement/setSelectedProduct', product)
                    this.applyProduct(product)
                } catch (error) {
                    if (version === this.version) this.error = errorMessage(error)
                } finally {
                    if (version === this.version) this.loading = false
                }
            },
            applyProduct(product) {
                this.form = { ...product }
                this.category = product.category ? { ...product.category } : null
                this.attributes = product.attributes.map(attribute => ({ ...attribute, selection: attribute.selection ? { ...attribute.selection } : null }))
                this.formVersion += 1
                this.baseline = JSON.stringify(this.payload)
                this.categoryErrors = []
                this.attributeErrors = {}
            },
            chooseCategory(category) {
                if ((category ? Number(category.ty_id) : null) === this.payload.category_id) { this.picker = false; return }
                this.category = category
                this.attributes = []
                this.attributeErrors = {}
                this.categoryErrors = []
                this.success = ''
                this.picker = false
                this.loadAttributes()
            },
            async loadAttributes() {
                this.version += 1
                const { version } = this
                this.attributes = []
                this.attributesError = ''
                this.attributesLoading = false
                if (!this.category) return
                this.attributesLoading = true
                try {
                    const attributes = await this.$store.dispatch('tyManagement/loadAttributes', this.category.ty_id)
                    if (version !== this.version) return
                    this.attributes = attributes.map(attribute => ({ ...attribute, selection: null }))
                    this.formVersion += 1
                } catch (error) {
                    if (version === this.version) this.attributesError = errorMessage(error)
                } finally {
                    if (version === this.version) this.attributesLoading = false
                }
            },
            setSelection(id, selection) {
                this.attributes = this.attributes.map(attribute => (attribute.attribute_id === id ? { ...attribute, selection } : attribute))
                this.success = ''
            },
            async save() {
                if (this.saving || this.attributesLoading || this.attributesError) return
                const { payload } = this
                this.saving = true
                this.error = ''
                this.success = ''
                this.categoryErrors = []
                this.attributeErrors = {}
                try {
                    const product = await this.$store.dispatch('tyManagement/saveProduct', { id: this.productId, payload })
                    this.applyProduct(product)
                    this.success = `Kaydedildi. Eksik zorunlu özellik sayısı: ${product.missing_required_attributes_count}`
                    this.$emit('saved')
                } catch (error) {
                    this.error = errorMessage(error)
                    const errors = (error.response && error.response.data && error.response.data.errors) || {}
                    this.categoryErrors = [].concat(errors.category_id || [])
                    Object.keys(errors).forEach(path => {
                        const match = path.match(/^attributes\.(\d+)(?:\.|$)/)
                        const row = match && payload.attributes[Number(match[1])]
                        if (row) this.$set(this.attributeErrors, row.attribute_id, [...(this.attributeErrors[row.attribute_id] || []), ...[].concat(errors[path])])
                        else if (path !== 'category_id') this.error += ` ${[].concat(errors[path]).join(' ')}`
                    })
                } finally {
                    this.saving = false
                }
            },
            async confirmClose() {
                if (this.saving) return false
                if (!this.dirty) return true
                return this.$bvModal.msgBoxConfirm('Kaydedilmemiş değişiklikler kaybolacak. Kapatılsın mı?', {
                    title: 'Kaydedilmemiş değişiklikler', okTitle: 'Değişiklikleri bırak', cancelTitle: 'Düzenlemeye devam et', okVariant: 'danger',
                })
            },
            onHide(event) {
                if (this.saving) { event.preventDefault(); return }
                if (!this.dirty) return
                event.preventDefault()
                this.confirmClose().then(confirmed => {
                    if (confirmed) { this.baseline = JSON.stringify(this.payload); this.$refs.modal.hide() }
                })
            },
            beforeUnload(event) {
                if (!this.dirty && !this.saving) return
                event.preventDefault()
                Object.assign(event, { returnValue: '' })
            },
            reset() {
                this.version += 1
                this.form = null
                this.loading = false
                this.attributesLoading = false
                this.category = null
                this.attributes = []
                this.picker = false
                this.attributesError = ''
                this.success = ''
                this.$store.commit('tyManagement/setSelectedProduct', null)
            },
        },
    }
</script>
